import React from 'react';
import { Box } from '@mui/material';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from 'recharts';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { TrendPoint } from '../useFeedData';

interface TrendChartCardProps {
  trendData: TrendPoint[];
}

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="custom-tooltip" style={{
        background: 'rgba(16, 23, 40, 0.9)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        padding: 12,
        borderRadius: 8,
        backdropFilter: 'blur(10px)',
      }}>
        <p style={{ fontSize: 12, color: '#f8fafc', marginBottom: 4 }}>Date: {label}</p>
        <p style={{ color: '#ff3b3b', fontWeight: 700, fontSize: 14, marginBottom: 4 }}>Criticals: {payload[1]?.value ?? 0}</p>
        <p style={{ fontSize: 11, color: '#6b7280' }}>No Skimmer: {payload[0]?.value ?? 0}</p>
      </div>
    );
  }
  return null;
};

export const TrendChartCard: React.FC<TrendChartCardProps> = ({ trendData }) => {
  return (
    <Box className="glass-panel data-card trend-chart-card">
      <div className="flex-between" style={{ marginBottom: '24px' }}>
        <h3 className="card-title" style={{ margin: 0 }}>Detection Trend</h3>
        <div className="select-date-range">
          <span>Reports Last 30 Days</span>
          <ChevronRightIcon sx={{ fontSize: 14 }} />
        </div>
      </div>

      <div style={{ width: '100%', height: 260 }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={trendData} margin={{ top: 10, right: 30, left: -20, bottom: 0 }}>
            <defs>
              <linearGradient id="colorHealthy" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#22d3ee" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#22d3ee" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="colorCritical" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#ff3b3b" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#ff3b3b" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
            <XAxis dataKey="name" stroke="rgba(255,255,255,0.3)" fontSize={12} tickMargin={10} axisLine={false} tickLine={false} />
            <YAxis stroke="rgba(255,255,255,0.3)" fontSize={12} axisLine={false} tickLine={false} />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,255,255,0.02)' }} />
            <Area
              type="monotone"
              dataKey="healthy"
              stroke="#22d3ee"
              fill="url(#colorHealthy)"
              strokeWidth={2}
              dot={{ r: 3, fill: '#22d3ee', strokeWidth: 0 }}
              activeDot={{ r: 6, fill: '#22d3ee', strokeWidth: 0, style: { filter: 'drop-shadow(0 0 8px #22d3ee)' } }}
            />
            <Area
              type="monotone"
              dataKey="critical"
              stroke="#ff3b3b"
              fill="url(#colorCritical)"
              strokeWidth={2}
              dot={{ r: 3, fill: '#ff3b3b', strokeWidth: 0 }}
              activeDot={{ r: 8, fill: '#ff3b3b', strokeWidth: 0, style: { filter: 'drop-shadow(0 0 12px #ff3b3b)' } }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="chart-footer">
        <div className="legend-items">
          <div className="legend-item">
            <span className="dot dot-cyan" style={{ width: 8, height: 8, borderRadius: '50%', background: '#22d3ee' }} />
            <span className="text-muted text-sm">No Skimmers Detected</span>
          </div>
          <div className="legend-item" style={{ marginLeft: 16 }}>
            <WarningAmberIcon sx={{ fontSize: 12, color: '#ff3b3b' }} />
            <span className="text-primary text-sm font-semibold">Skimmers Detected</span>
          </div>
        </div>
        <span className="text-muted text-sm" style={{ letterSpacing: '0.5px' }}>Detection frequency over 30 days</span>
      </div>
    </Box>
  );
};
